import React, { useMemo, useState } from "react";
import { Table, Checkbox, InputNumber, Input, Tag, Tooltip, Radio, Space } from "antd";
import { ReloadOutlined, UndoOutlined, SyncOutlined, FileExcelOutlined } from "@ant-design/icons";
import { ButtonItem } from "../../../../../Components/Items";
import {
  fmtMXN, filaIncluida, redondear2, diasProporcionales, fmtDias, fmtProporcion,
} from "../aguinaldoUtils";
import DetalleCalculoAguinaldo from "../DetalleCalculoAguinaldo";

const FILTROS = [
  { label: "Todos", value: "todos" },
  { label: "Incluidos", value: "incluidos" },
  { label: "No incluidos", value: "fuera" },
  { label: "Con aviso", value: "aviso" },
];

const tieneAviso = (e) => !!(e.noAplicable || e.yaTieneAguinaldo || e.sinTablaPrestacion);

/**
 * Paso 3 — la tabla de empleados. Se pueden quitar o agregar empleados y corregir las incidencias
 * o los días trabajados (el importe se recalcula) o el importe directamente (queda marcado como
 * manual). Lo mismo se puede hacer en Excel (descargar, editar y cargar), y "Resetear líneas"
 * sincroniza el borrador con el padrón (bajas y altas) sin tocar a los demás.
 * Cada renglón enseña el cálculo completo: días del ejercicio, días de aguinaldo de la tabla,
 * proporción del año y días a pagar; al desplegarlo sale el desglose paso por paso.
 */
const PasoEmpleados = ({
  empleados, tipoAplicacion, descontarIncidencias, nombrePrenomina, cargando, soloLectura,
  diasAnio, ejercicio, fechaCorte,
  onCambiarFila, onRestaurarImporte, onIncluirTodos, onRecalcular, onResetear, onExcel,
}) => {
  const [busqueda, setBusqueda] = useState("");
  const [filtro, setFiltro] = useState("todos");

  const visibles = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return (empleados || []).filter((e) => {
      if (q && !`${e.nombre} ${e.codigo ?? ""}`.toLowerCase().includes(q)) return false;
      if (filtro === "incluidos") return filaIncluida(e);
      if (filtro === "fuera") return !filaIncluida(e);
      if (filtro === "aviso") return tieneAviso(e);
      return true;
    });
  }, [empleados, busqueda, filtro]);

  const incluidos = (empleados || []).filter(filaIncluida);
  const total = redondear2(incluidos.reduce((acc, e) => acc + (Number(e.importe) || 0), 0));
  const seleccionables = (empleados || []).filter((e) => !e.noAplicable);
  const todosMarcados = seleccionables.length > 0 && seleccionables.every((e) => e.incluido);
  const algunoMarcado = seleccionables.some((e) => e.incluido);

  const columnas = [
    {
      title: (
        <Checkbox
          checked={todosMarcados}
          indeterminate={algunoMarcado && !todosMarcados}
          disabled={soloLectura}
          onChange={(ev) => onIncluirTodos(ev.target.checked)}
        />
      ),
      key: "incluido",
      width: 44,
      align: "center",
      render: (_, e) => (
        <Tooltip title={e.noAplicable || ""}>
          <Checkbox
            checked={!!e.incluido}
            disabled={soloLectura || !!e.noAplicable}
            onChange={(ev) => onCambiarFila(e.empleadoId, { incluido: ev.target.checked })}
          />
        </Tooltip>
      ),
    },
    {
      title: "Empleado",
      key: "empleado",
      width: 240,
      render: (_, e) => (
        <div>
          <div className="aguinaldo-fuerte">{e.nombre}</div>
          {e.codigo !== undefined && e.codigo !== null && (
            <span className="aguinaldo-tenue">No. {String(e.codigo).padStart(4, "0")}</span>
          )}
        </div>
      ),
    },
    ...(tipoAplicacion === "ordinaria"
      ? [{
        title: "Prenómina",
        key: "prenomina",
        width: 200,
        ellipsis: true,
        render: (_, e) => (e.prenominaId ? nombrePrenomina(e.prenominaId) : <span className="aguinaldo-tenue">—</span>),
      }]
      : []),
    {
      title: "Días de aguinaldo (tabla)",
      children: [
        { title: "Antig.", dataIndex: "antiguedadAnios", width: 60, align: "center" },
        {
          title: "Días",
          dataIndex: "diasAguinaldo",
          width: 64,
          align: "center",
          render: (d, e) => (e.sinTablaPrestacion
            ? <Tooltip title="Sin tabla de prestaciones para su antigüedad: se proponen los 15 días de ley."><span className="aguinaldo-aviso-texto">{d}*</span></Tooltip>
            : <Tooltip title={e.tablaPrestacion ? `Tabla "${e.tablaPrestacion}"${e.anioTabla ? `, año de servicio ${e.anioTabla}` : ""}` : ""}>{d}</Tooltip>),
        },
      ],
    },
    {
      title: "Días trabajados en el año",
      children: [
        {
          title: "Del ejercicio",
          dataIndex: "diasCalendario",
          width: 80,
          align: "center",
          render: (d, e) => (e.diasTopados
            ? <Tooltip title={`Topado a los ${diasAnio} días del año`}>{d}*</Tooltip>
            : d),
        },
        ...(descontarIncidencias
          ? [{
            title: "Incid.",
            key: "diasIncidencias",
            width: 86,
            align: "center",
            render: (_, e) => (
              <Tooltip title={e.incidenciasManual ? "Incidencias corregidas a mano" : ""}>
                <InputNumber
                  size="small"
                  min={0}
                  max={e.diasCalendario || 0}
                  step={1}
                  value={e.diasIncidencias}
                  disabled={soloLectura}
                  className={e.incidenciasManual ? "aguinaldo-importe-manual" : ""}
                  onChange={(v) => onCambiarFila(e.empleadoId, { diasIncidencias: v ?? 0 }, "incidencias")}
                  style={{ width: 66 }}
                />
              </Tooltip>
            ),
          }]
          : []),
        {
          title: "Trabajados",
          key: "diasTrabajados",
          width: 96,
          align: "center",
          render: (_, e) => (
            <InputNumber
              size="small"
              min={0}
              max={366}
              step={1}
              value={e.diasTrabajados}
              disabled={soloLectura}
              onChange={(v) => onCambiarFila(e.empleadoId, { diasTrabajados: v ?? 0 }, "dias")}
              style={{ width: 76 }}
            />
          ),
        },
      ],
    },
    {
      title: `Proporcional (÷ ${diasAnio})`,
      children: [
        {
          title: "% del año",
          key: "proporcion",
          width: 82,
          align: "center",
          render: (_, e) => fmtProporcion(e.diasTrabajados, diasAnio),
        },
        {
          title: "Días a pagar",
          key: "diasPagar",
          width: 92,
          align: "center",
          render: (_, e) => (
            <Tooltip title={`${e.diasAguinaldo} × ${e.diasTrabajados} ÷ ${diasAnio}`}>
              <b>{fmtDias(diasProporcionales(e.diasAguinaldo, diasAnio, e.diasTrabajados))}</b>
            </Tooltip>
          ),
        },
      ],
    },
    {
      title: "Salario diario",
      dataIndex: "salarioDiario",
      width: 110,
      align: "right",
      render: (n) => fmtMXN(n),
    },
    {
      title: "Importe",
      key: "importe",
      width: 160,
      align: "right",
      render: (_, e) => (
        <Space size={4}>
          <InputNumber
            size="small"
            min={0}
            step={0.01}
            precision={2}
            value={e.importe}
            disabled={soloLectura}
            className={e.importeManual ? "aguinaldo-importe-manual" : ""}
            onChange={(v) => onCambiarFila(e.empleadoId, { importe: v ?? 0 }, "importe")}
            style={{ width: 110 }}
          />
          {e.importeManual && !soloLectura && (
            <Tooltip title="Importe capturado a mano. Clic para volver a la fórmula.">
              <UndoOutlined className="aguinaldo-accion" onClick={() => onRestaurarImporte(e.empleadoId)} />
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: "Avisos",
      key: "avisos",
      width: 260,
      render: (_, e) => (
        <div className="aguinaldo-avisos">
          {e.noAplicable && <Tag color="red">{e.noAplicable}</Tag>}
          {e.yaTieneAguinaldo && (
            <Tooltip title={e.yaTieneAguinaldo}>
              <Tag color="orange">Ya tiene aguinaldo</Tag>
            </Tooltip>
          )}
          {e.yaTieneAguinaldo && e.incluido && (
            <div className="aguinaldo-error-texto">Se le pagaría otra vez.</div>
          )}
          {e.importeManual && <Tag>Importe manual</Tag>}
        </div>
      ),
    },
  ];

  return (
    <div className="aguinaldo-paso">
      <div className="aguinaldo-bandeja-cabecera">
        <div>
          <div className="aguinaldo-titulo">Empleados</div>
          <div className="aguinaldo-formula">
            Días a pagar = días de aguinaldo × días trabajados ÷ <b>{diasAnio}</b> días del año
            <span className="aguinaldo-formula-sep">·</span>
            Importe = días a pagar × salario diario
          </div>
          <div className="aguinaldo-tenue">
            El divisor del año sale de Configuración de Nómina (Proporcionales de Prestaciones). Despliega
            un renglón (+) para ver su cálculo completo. Marca a quién se le aplica y corrige los días o el
            importe si hace falta.
          </div>
        </div>
        {!soloLectura && (
          <Space wrap>
            <Tooltip title="Descarga la tabla a Excel, edítala y vuelve a cargarla">
              <ButtonItem icon={<FileExcelOutlined />} onClick={onExcel} disabled={cargando || !(empleados || []).length}>
                Excel
              </ButtonItem>
            </Tooltip>
            <Tooltip title="Quita a los que se dieron de baja y agrega a los nuevos, sin tocar a los demás">
              <ButtonItem icon={<SyncOutlined />} onClick={onResetear} disabled={cargando || !(empleados || []).length}>
                Resetear líneas
              </ButtonItem>
            </Tooltip>
            <ButtonItem icon={<ReloadOutlined />} onClick={onRecalcular} loading={cargando}>
              Recalcular propuesta
            </ButtonItem>
          </Space>
        )}
      </div>

      <div className="aguinaldo-filtros">
        <Input.Search
          allowClear
          placeholder="Buscar empleado"
          style={{ width: 260 }}
          value={busqueda}
          onChange={(ev) => setBusqueda(ev.target.value)}
        />
        <Radio.Group
          options={FILTROS}
          optionType="button"
          value={filtro}
          onChange={(ev) => setFiltro(ev.target.value)}
        />
        <div className="aguinaldo-resumen-linea">
          <b>{incluidos.length}</b> incluido(s) de {(empleados || []).length} · Total <b>{fmtMXN(total)}</b>
        </div>
      </div>

      <Table
        rowKey={(e) => String(e.empleadoId)}
        size="small"
        loading={cargando}
        columns={columnas}
        dataSource={visibles}
        pagination={{ pageSize: 50, hideOnSinglePage: true, showSizeChanger: false }}
        scroll={{ x: 1500 }}
        bordered
        rowClassName={(e) => (filaIncluida(e) ? "" : "aguinaldo-fila-fuera")}
        expandable={{
          expandedRowRender: (e) => (
            <DetalleCalculoAguinaldo e={e} diasAnio={diasAnio} ejercicio={ejercicio} fechaCorte={fechaCorte} />
          ),
        }}
      />
    </div>
  );
};

export default PasoEmpleados;
